/**
 * Functionality to update the store when a Content has been processed.
 *
 * That content will be either a RadioSpot or a TVSpotBundle, see
 * websockets.py for the backend counterpart. It could also be an
 * EditableTVSpotVideo, but these have their own mixin and are not
 * handled here.
 *
 * Depends on:
 *   - this.$api.retrieveContent()
 *
 * Use like:
 *   Inside mounted(), hook up the main method to the associated event
 *   which will be emmitted by the socketbridge:
 *
 *     this.$bus.$on('refetch-on-content-processed',
 *                   this.updateStoreRefsOnContentProcessed);
 *
 *   Or, if the component has its own ref to the changed Content, make
 *   up you own handler like:
 *
 *     this.$bus.$on('refetch-on-content-processed', content => {
 *       // if content matches ref, refetch it...
 *     });
 */
const updateOnContentProcessed = {
  computed: {
    /**
     * Return combined store.contents + store.latestSpots as flat list.
     */
    combinedContentLists() {
      return []
        .concat(this.$store.state.common.contents)
        .concat(this.$store.state.common.latestSpots);
    },
  },
  methods: {
    showContentProcessedNotification(content) {
      const message = this.$t('contentProcessed') + ': ';
      this.$bus.$emit('alert-info', {
        message: message,
        link: {
          text: content.title,
          path: `/spot/${content.ctype}/${content.id}`,
        },
        timeout: 10000,
      });
    },
    /**
     * Check if content equals given object.
     */
    isContentSameAs(content, obj) {
      if (!content || !obj) {
        return false;
      }
      const matchesSpotRef = content.kind === obj.kind && content.id === obj.id;
      return matchesSpotRef;
    },
    /**
     * Check if content is on store's content lists.
     */
    isContentReferencedInStoreLists(content) {
      const numRefs = this.combinedContentLists.filter((storeContent) =>
        this.isContentSameAs(content, storeContent)
      ).length;
      const isReferenced = numRefs > 0;
      return isReferenced;
    },
    /**
     * Refetch processed Content into the store as needed.
     *
     * The store contains multiple lists of contents that may or may
     * not include the content that changed. If it did, refetch it and
     * make sure that new object is replaced in those lists.
     */
    updateStoreRefsOnContentProcessed(processedContent) {
      if (processedContent.kind === 'editabletvspotvideo') {
        return;
      }
      const updateNeeded = this.isContentReferencedInStoreLists(
        processedContent
      );
      if (!updateNeeded) {
        return;
      }

      // Fetch the Content (also updates it in the store's contents list).
      this.$api
        .retrieveContent(processedContent.ctype, processedContent.id)
        .then(() => {
          this.showContentProcessedNotification(processedContent);
        });
    },
  },
};

export default updateOnContentProcessed;



// WEBPACK FOOTER //
// ./src/mixins/updateOnContentProcessed.js